/**
 * Signup Intent Tracking (Client-Side Utilities)
 *
 * This module provides client-side utilities for tracking whether a user is
 * signing up as a researcher or participant. This determines whether we
 * auto-create an organization for them.
 *
 * Server-side signup intent operations are handled by the API:
 * - auth.getSignupIntent - Get signup intent from cookies or user metadata
 * - auth.setSignupIntent - Set signup intent cookie
 */

import { SIGNUP_INTENT_COOKIE } from "@/lib/signup-cookie-names";

const SIGNUP_INTENT_EXPIRY = 60 * 60 * 1000; // 1 hour

export type SignupIntent = "researcher" | "participant";

/**
 * Set signup intent in cookie (client-side)
 */
export function setSignupIntent(intent: SignupIntent) {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(Date.now() + SIGNUP_INTENT_EXPIRY);
  document.cookie = `${SIGNUP_INTENT_COOKIE}=${intent}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

  console.log("📝 Signup intent set:", intent);
}

/**
 * Get signup intent from cookie (client-side)
 */
export function getSignupIntent(): SignupIntent | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(
    new RegExp("(^| )" + SIGNUP_INTENT_COOKIE + "=([^;]+)"),
  );
  const intent = match ? (match[2] as SignupIntent) : null;

  console.log("📖 Retrieved signup intent:", intent);
  return intent;
}

/**
 * Clear signup intent cookie (client-side)
 */
export function clearSignupIntent() {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${SIGNUP_INTENT_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  console.log("🗑️ Signup intent cleared");
}

/**
 * Infer signup intent from context if not explicitly set
 */
export function inferSignupIntent(context: {
  returnUrl?: string | null;
  redirectTo?: string | null;
  fromInterviewPage?: boolean;
}): SignupIntent {
  const { returnUrl, redirectTo, fromInterviewPage } = context;

  // If coming from interview, they're a participant
  if (fromInterviewPage) {
    console.log("🎯 Inferred signup intent: participant (from interview)");
    return "participant";
  }

  // If redirecting from /interview pages, they're a participant
  if (returnUrl?.includes("/interview") || redirectTo?.includes("/interview")) {
    console.log(
      "🎯 Inferred signup intent: participant (from /interview redirect)",
    );
    return "participant";
  }

  // If redirecting to /org, they're a researcher
  if (returnUrl?.includes("/org") || redirectTo?.includes("/org")) {
    console.log("🎯 Inferred signup intent: researcher (from /org redirect)");
    return "researcher";
  }

  // Any researcher surface (the /researchers marketing page, the org-setup
  // wizard) means they came to run research, not to be interviewed. Without
  // this a researcher who reaches the expert /login with returnUrl=/researchers
  // falls through to the participant default and gets routed to the expert area.
  if (
    returnUrl?.includes("/researchers") ||
    redirectTo?.includes("/researchers")
  ) {
    console.log(
      "🎯 Inferred signup intent: researcher (from /researchers redirect)",
    );
    return "researcher";
  }

  // If redirecting to /me, they're a participant
  if (returnUrl?.includes("/me") || redirectTo?.includes("/me")) {
    console.log("🎯 Inferred signup intent: participant (from /me redirect)");
    return "participant";
  }

  // Default to participant (safer - they can upgrade later)
  console.log("🎯 Inferred signup intent: participant (default)");
  return "participant";
}
